// packages/shared/src/villagers/schedule.ts — deterministic villager routines and the one pose path (spec §9.13, rev 5 item 13)
import { tuning } from '@whiskerton/content';
import { groundAt } from '../map/codec';
import { findPath } from '../map/nav';
import { rngFrom } from '../rng';
import { dayKeyOf, dayStartMs, townTime } from '../time';
import type { TileXY, Vec2 } from '../types/geom';
import type { DayKey } from '../types/ids';
import type { GeneratedTown, MapData, NavGrid, Plot } from '../types/map';
import type { DaySchedule, PlaceId, ScheduleSeg, VillagerIdle, VillagerPause, VillagerPublic } from '../types/villagers';
import { scheduleNav } from './nav';
import { applyPauses } from './pause';

export const DAY_START_MIN = tuning.time.dayStartHour * 60;
export const DAY_END_MIN = DAY_START_MIN + 24 * 60;
export const toScheduleMinute = (minuteOfDay: number) => (minuteOfDay < DAY_START_MIN ? minuteOfDay + 24 * 60 : minuteOfDay);
/** The fractional wall-clock minute of day. Server and client must pass this, never the integer TownTime.minuteOfDay. */
export const scheduleMinuteAt = (nowMs: number, timeZone: string): number => townTime(nowMs, timeZone).minuteOfDay + (nowMs % 60_000) / 60_000;

/** `museum` and `shop`: the spots beside that door (doorSpots), not the door itself. */
export interface PlaceTargets { home: TileXY; plaza: TileXY[]; beach: TileXY[]; river: TileXY[]; museum: TileXY[]; shop: TileXY[]; nearHouse: TileXY[] }
export interface VillagerPose { visible: boolean; pos: Vec2; f: number; anim: 'walk' | VillagerIdle }
type Walk = Extract<ScheduleSeg, { kind: 'walk' }>;
type Outing = Exclude<PlaceId, 'home'>;

const PLACES: readonly Outing[] = ['plaza', 'beach', 'river', 'museum', 'shop', 'nearHouse'];
const IDLES: Record<Outing, readonly VillagerIdle[]> = {
  plaza: ['stand', 'lookAround', 'stretch'], beach: ['lookAround', 'stretch', 'sit'], river: ['fish', 'sit', 'lookAround'],
  museum: ['read', 'lookAround'], shop: ['stand', 'lookAround'], nearHouse: ['stand', 'stretch', 'lookAround'],
};
const LAND = new Set(['grass', 'sand', 'path']);
const center = (t: TileXY): Vec2 => ({ x: t.x + 0.5, y: t.y + 0.5 });
/** Beside a door: along the building's front and the two rows before it, up to three tiles either side, nearest first. Never the
 *  door's own column, the way in (v1 round 3 playtest: two villagers idled inside each other on the museum's door tile). Eighteen
 *  spots, so a full town (tuning.town.plots) keeps one walkable spot per plot even where a sign or a tree takes a few. */
const BESIDE_DOOR: readonly (readonly [number, number])[] = [0, 1, 2].flatMap((dy) => [1, 2, 3].flatMap((dx) => [[-dx, dy], [dx, dy]] as const));
export const doorSpots = (door: TileXY): TileXY[] => BESIDE_DOOR.map(([dx, dy]) => ({ x: door.x + dx, y: door.y + dy }));
const AT_DOOR = new Set<Outing>(['museum', 'shop']);

export function placeTargets(map: MapData, plot: Plot): PlaceTargets {
  const { plaza, beach, museumDoor, shopDoor } = map.sites;
  const plazaTiles: TileXY[] = [];
  for (let y = plaza.y; y < plaza.y + plaza.h; y++) for (let x = plaza.x; x < plaza.x + plaza.w; x++) if (LAND.has(groundAt(map, x, y))) plazaTiles.push({ x, y });
  const beachTiles: TileXY[] = [];
  for (const r of beach) for (let y = r.y; y < r.y + r.h; y += 2) for (let x = r.x; x < r.x + r.w; x += 2) if (groundAt(map, x, y) === 'sand') beachTiles.push({ x, y });
  const river: TileXY[] = [];
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    if (LAND.has(groundAt(map, x, y)) && [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => groundAt(map, x + dx!, y + dy!) === 'river')) river.push({ x, y });
  }
  const nearHouse = map.plots.filter((p) => p.index !== plot.index).map((p) => ({ x: p.door.x, y: p.door.y + 1 }));
  return { home: plot.door, plaza: plazaTiles, beach: beachTiles, river: river.filter((_, i) => i % 3 === 0), museum: doorSpots(museumDoor), shop: doorSpots(shopDoor), nearHouse };
}

const walkable = (nav: NavGrid, t: TileXY) => t.x >= 0 && t.y >= 0 && t.x < nav.w && t.y < nav.h && nav.walk[t.y * nav.w + t.x] === 1;
function route(nav: NavGrid, map: MapData, from: TileXY, to: TileXY): TileXY[] | null {
  const path = findPath(nav, map, from, to);
  if (!path) return null;
  const out = [...path];
  if (out.length === 0 || out[0]!.x !== from.x || out[0]!.y !== from.y) out.unshift(from);
  const last = out[out.length - 1]!;
  if (last.x !== to.x || last.y !== to.y) out.push(to);
  return out;
}
export function pathLength(path: readonly TileXY[]): number {
  let d = 0;
  for (let i = 1; i < path.length; i++) d += Math.hypot(path[i]!.x - path[i - 1]!.x, path[i]!.y - path[i - 1]!.y);
  return d;
}
const minutesFor = (path: readonly TileXY[]) => pathLength(path) / tuning.villagers.walkSpeed / 60;

export function villagerSchedule(townSeed: number, v: VillagerPublic, dayKey: DayKey, nav: NavGrid, map: MapData, places: PlaceTargets): DaySchedule {
  const vt = tuning.villagers;
  const rng = rngFrom('vday', townSeed, v.id, dayKey);
  const wake = rng.int(vt.wakeFrom, vt.wakeTo);
  const homeAt = rng.int(vt.homeFrom, vt.homeTo);
  const want = rng.int(vt.stops[0], vt.stops[1]);
  const stops: { at: TileXY; place: Outing; idle: VillagerIdle; path: TileXY[]; weight: number }[] = [];
  let cur = places.home;
  for (let tries = 0; stops.length < want && tries < want * 4; tries++) {
    const place = rng.pick(PLACES);
    const idle = rng.pick(IDLES[place]);
    const weight = rng.int(vt.idleMinutes[0], vt.idleMinutes[1]);
    const cands = places[place].filter((t) => walkable(nav, t));
    if (cands.length === 0) continue;
    const picked = rng.pick(cands);
    // By a door, each villager keeps its own spot (by plot: one villager a plot, and there are at least as many walkable spots as
    // plots), so several idle side by side, not in one another.
    const at = AT_DOOR.has(place) ? cands[v.plot % cands.length]! : picked;
    if (at.x === cur.x && at.y === cur.y) continue;
    const path = route(nav, map, cur, at);
    if (!path) continue;
    stops.push({ at, place, idle, path, weight });
    cur = at;
  }
  // The walk home must exist and every idle must keep its minimum length: drop stops from the end until both hold.
  let back: TileXY[] | null = null;
  while (stops.length > 0) {
    back = route(nav, map, stops[stops.length - 1]!.at, places.home);
    const walking = stops.reduce((s, st) => s + minutesFor(st.path), 0) + (back ? minutesFor(back) : 0);
    if (back && homeAt - wake - walking >= stops.length * vt.idleMinutes[0]) break;
    stops.pop();
    back = null;
  }
  const segs: ScheduleSeg[] = [{ kind: 'home', from: DAY_START_MIN, to: wake }];
  if (stops.length > 0 && back) {
    const walks = stops.map((s) => minutesFor(s.path));
    const avail = homeAt - wake - walks.reduce((a, b) => a + b, 0) - minutesFor(back);
    const totalWeight = stops.reduce((a, s) => a + s.weight, 0);
    let t = wake;
    stops.forEach((s, i) => {
      segs.push({ kind: 'walk', from: t, to: t + walks[i]!, path: s.path });
      t += walks[i]!;
      const d = (avail * s.weight) / totalWeight;
      segs.push({ kind: 'idle', from: t, to: t + d, at: s.at, place: s.place, idle: s.idle });
      t += d;
    });
    segs.push({ kind: 'walk', from: t, to: homeAt, path: back });
    segs.push({ kind: 'home', from: homeAt, to: DAY_END_MIN });
  } else {
    segs.push({ kind: 'home', from: wake, to: DAY_END_MIN });
  }
  return { villager: v.id, dayKey, segs };
}

export function pointAlong(path: readonly TileXY[], dist: number): { pos: Vec2; f: number } {
  if (path.length < 2) return { pos: center(path[0] ?? { x: 0, y: 0 }), f: 0 };
  let left = Math.max(0, dist);
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!;
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
    if (left <= len || i === path.length - 1) {
      const k = len === 0 ? 0 : Math.min(1, left / len);
      return { pos: { x: a.x + 0.5 + dx * k, y: a.y + 0.5 + dy * k }, f: Math.atan2(dx, dy) };
    }
    left -= len;
  }
  return { pos: center(path[path.length - 1]!), f: 0 };
}
export function walkPose(g: Walk, minute: number): { pos: Vec2; f: number } {
  const [a, b] = g.span ?? [0, 1];
  const k = g.to > g.from ? Math.min(1, Math.max(0, (minute - g.from) / (g.to - g.from))) : 1;
  return pointAlong(g.path, (a + (b - a) * k) * pathLength(g.path));
}
export function segAt(s: DaySchedule, minute: number): ScheduleSeg {
  return s.segs.find((g) => minute >= g.from && minute < g.to) ?? s.segs[s.segs.length - 1]!;
}
const homeTile = (s: DaySchedule): TileXY => s.segs.find((g): g is Walk => g.kind === 'walk' && g.path.length > 0)?.path[0] ?? { x: 0, y: 0 };

export function poseAt(s: DaySchedule, minuteOfDay: number): VillagerPose {
  const m = toScheduleMinute(minuteOfDay);
  const g = segAt(s, m);
  if (g.kind === 'home') return { visible: false, pos: center(homeTile(s)), f: 0, anim: 'stand' };
  if (g.kind === 'walk') return { visible: true, ...walkPose(g, m), anim: 'walk' };
  return { visible: true, pos: g.pos ?? center(g.at), f: g.f ?? 0, anim: g.idle };
}
/** Home after the day's outing (never the morning home segment) and before bedtime. */
export function isHomeAwake(s: DaySchedule, minuteOfDay: number): boolean {
  const m = toScheduleMinute(minuteOfDay);
  const g = segAt(s, m);
  return g.kind === 'home' && g !== s.segs[0] && m < tuning.villagers.bedtime;
}
export const villagerRoomDoor = (room: readonly [number, number]): TileXY => ({ x: Math.floor(room[0] / 2), y: room[1] - 1 });
export const villagerIndoorSpot = (room: readonly [number, number]): Vec2 => ({ x: Math.floor(room[0] / 2) + 0.5, y: room[1] - 2 + 0.5 });

/** places[i] for plot i — the single loop both the server (M2-T06) and the client (M2-T11) use. */
export const villagerPlaces = (gen: GeneratedTown): PlaceTargets[] => gen.map.plots.map((p) => placeTargets(gen.map, p));
export interface ScheduleWorld { gen: GeneratedTown; nav: NavGrid; places: PlaceTargets[] }
/** D-40: built once per town from generateTown(seed, { genVersion }) — never from the live object list. */
export const scheduleWorld = (gen: GeneratedTown): ScheduleWorld => ({ gen, nav: scheduleNav(gen), places: villagerPlaces(gen) });

/** The one pose path (rev 5 item 13). villagerSchedule (10–25 A* searches) runs once per (villager, plot, day);
 *  pauses change often (every talk) and are cheap, so applyPauses runs on top of the cached schedule on each call. */
export class VillagerPoseCache {
  private readonly days = new Map<string, DaySchedule>();
  private readonly world: ScheduleWorld;
  private readonly townSeed: number;
  private readonly timeZone: string;
  private readonly maxDays: number;
  constructor(world: ScheduleWorld, townSeed: number, timeZone: string, maxDays = 256) {
    this.world = world;
    this.townSeed = townSeed;
    this.timeZone = timeZone;
    this.maxDays = maxDays;
  }
  schedule(v: VillagerPublic, dayKey: DayKey): DaySchedule {
    const key = `${v.id}|${v.plot}|${dayKey}`;
    let s = this.days.get(key);
    if (!s) {
      if (this.days.size >= this.maxDays) this.days.clear();
      s = villagerSchedule(this.townSeed, v, dayKey, this.world.nav, this.world.gen.map, this.world.places[v.plot]!);
      this.days.set(key, s);
    }
    return s;
  }
  poseAt(v: VillagerPublic, pauses: readonly VillagerPause[], nowMs: number): VillagerPose & { homeAwake: boolean } {
    const dayKey = dayKeyOf(nowMs, this.timeZone);
    const s = applyPauses(this.schedule(v, dayKey), pauses, dayStartMs(dayKey, this.timeZone));   // filters to this villager and day
    const m = scheduleMinuteAt(nowMs, this.timeZone);
    return { ...poseAt(s, m), homeAwake: isHomeAwake(s, m) };
  }
}
